import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import UserCard from '../../../components/UserCard';
import { GET_MEMBER } from '../../../common/graphql/GetMember';
import { useQuery, useMutation } from 'react-apollo-hooks';
import get from 'lodash/get';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Divider from '@material-ui/core/Divider';
import Button from '../../../components/Button';
import { Map } from 'immutable';
import MyTeamContext from '../../../contexts/MyTeamContext';
import TargetsForm from './TargetsForm';
import SwipeableViews from 'react-swipeable-views';
import UserForm from '../../../components/CreateUserDialog/UserForm';
import FeedbackForm from '../../../components/FeedbackForm';
import Snackbar from '../../../components/Snackbar';
import { Typography } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import PerformanceTab from './PerformanceTab';

export const MemberDetailsContext = React.createContext();

const styles = theme => ({});

const DialogHeader = () => {
  const [state] = React.useContext(MemberDetailsContext);
  const { state: myTeamContextState, service } = React.useContext(MyTeamContext);

  const handlers = {
    onTabChange(event, value) {
      service.send({
        type: 'CHANGE_TAB',
        value: {
          dialog: {
            ...get(myTeamContextState, 'context.dialog', {}),
            tabIndex: value,
          },
        },
      });
    },
  };
  const member = state.get('member');
  if (!member)
    return (
      <>
        <div style={{ padding: 25, paddingBottom: 0 }}>
          <Typography variant="h5" gutterBottom>
            New Member
          </Typography>
        </div>
        <Divider variant="middle" />
      </>
    );

  return (
    <>
      <UserCard user={member} transparent inline />
      <Tabs
        scrollButtons="auto"
        variant="scrollable"
        onChange={handlers.onTabChange}
        value={get(myTeamContextState, 'context.dialog.tabIndex', 0)}
        indicatorColor="primary"
        textColor="primary"
      >
        {state.tabs.map((tab, ii) => {
          return <Tab key={ii} label={tab.label} />;
        })}
      </Tabs>
      <Divider variant="middle" />
    </>
  );
};

function MemberDetailsDialog(props) {
  const { fullScreen } = props;
  const [message, setMessage] = React.useState({
    type: 'info',
    show: false,
    content: '',
  });
  const context = React.useState(Map({}));
  const { state, service } = React.useContext(MyTeamContext);
  const selectedTabIndex = get(state, 'context.dialog.tabIndex', 0);
  const dialogData = get(state, 'context.dialog');
  const { focusField, refPlanResultId, tabIndex, memberId } = dialogData || {};
  // if dialog data is saved in url, open it now
  React.useEffect(() => {
    if (!state.matches('showList')) return;
    if (!dialogData) return;
    service.send({
      type: 'OPEN_MEMBER_DIALOG',
      value: {
        dialog: dialogData,
      },
    });
  }, [dialogData, state.value]);

  const handlers = {
    handleClose: React.useCallback(() => {
      service.send({
        type: 'CLOSE',
        value: { dialog: undefined },
      });
    }, []),
    onFormChanged: React.useCallback(data => {}, []),
  };

  const { data, loading } = useQuery(GET_MEMBER.query, {
    variables: { memberId },
    skip: !memberId,
  });

  const handleMessageClose = () => {
    setMessage({ ...message, show: false });
  };

  const member = get(data, 'getMember');

  const open = state.matches('viewMemberDialog');
  React.useEffect(() => {
    const [, setState] = context;
    if (!open) {
      setState(oldState => oldState.merge({ focusField: null, member: null }));
      return;
    }
    setState(oldState =>
      oldState.merge({
        focusField,
        member,
      }),
    );
  }, [focusField, member, open]);

  const handleChangeIndex = React.useCallback(
    index => {
      service.send({
        type: 'CHANGE_TAB',
        value: {
          dialog: {
            ...get(state, 'context.dialog', {}),
            tabIndex: index,
          },
        },
      });
    },
    [service, state],
  );

  const onUserFormSubmit = React.useCallback(() => {
    handlers.handleClose();
  }, [handlers]);

  const tabs = [
    {
      label: 'Profile',
      component: (
        <UserForm
          key={'userform-' + memberId}
          setMessage={setMessage}
          memberId={memberId}
          onSubmit={onUserFormSubmit}
        />
      ),
    },
    { label: 'Targets', component: <TargetsForm planResultId={refPlanResultId} /> },
    {
      label: 'Plan & Feedback',
      component: <FeedbackForm planResultId={refPlanResultId} />,
    },
    {
      label: 'Performance',
      component: <PerformanceTab planResultId={refPlanResultId} />,
    },
  ];

  const contextState = React.useMemo(() => {
    const [state, setState] = context;
    state.tabs = tabs;
    return [state, setState, handlers];
  }, [context, handlers, tabs]);
  return (
    <MemberDetailsContext.Provider value={contextState}>
      <Dialog
        fullScreen={fullScreen}
        open={open}
        scroll="body"
        onClose={handlers.handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        {loading && (
          <DialogContent>
            <CircularProgress />
          </DialogContent>
        )}
        {!loading && (
          <>
            <DialogTitle id="responsive-dialog-title" style={{ padding: 0 }}>
              <DialogHeader />
            </DialogTitle>
            <SwipeableViews
              axis={'x'}
              index={tabIndex}
              animateHeight
              onChangeIndex={handleChangeIndex}
              action={actions => {
                const [, setState] = context;
                setState(oldState => oldState.merge({ swipeableActions: actions }));
              }}
            >
              {tabs.map((tab, ii) => {
                if (selectedTabIndex !== ii) return <div key={ii} />;
                return <DialogContent key={ii}>{tab.component}</DialogContent>;
              })}
            </SwipeableViews>
          </>
        )}
      </Dialog>
      <Snackbar
        variant={message.type}
        open={message.show}
        onClose={handleMessageClose}
        autoHideDuration={5000}
        message={message.content}
      />
    </MemberDetailsContext.Provider>
  );
}

export default withStyles(styles)(MemberDetailsDialog);
